import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaChevronDown, FaGlobe } from 'react-icons/fa';
import { useTranslation } from '../../hooks/useTranslation';

const SelectorContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const SelectorButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.theme === 'dark' 
    ? 'rgba(51, 65, 85, 0.8)' 
    : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(255, 255, 255, 0.2)'};
  color: ${props => props.theme === 'dark' ? '#f8fafc' : '#fff'};
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  font-size: 0.875rem;
  backdrop-filter: blur(10px);
  min-width: ${props => props.$compact ? 'auto' : '120px'};
  
  &:hover {
    background: ${props => props.theme === 'dark' 
      ? 'rgba(71, 85, 105, 0.9)' 
      : 'rgba(255, 255, 255, 0.15)'};
    border-color: #e63946;
    transform: translateY(-1px);
  }
  
  .flag {
    font-size: 1rem;
    line-height: 1;
  }
  
  .chevron {
    font-size: 0.75rem;
    transition: transform 0.3s ease;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    min-width: auto;
    
    .language-text {
      display: ${props => props.$compact ? 'none' : 'block'};
    }
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: ${props => props.theme === 'dark' 
    ? 'rgba(15, 23, 42, 0.95)' 
    : 'rgba(0, 0, 0, 0.95)'};
  backdrop-filter: blur(20px);
  border: 1px solid ${props => props.theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 0.5rem;
  min-width: 150px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transform: ${props => props.$isOpen 
    ? 'translateY(0) scale(1)' 
    : 'translateY(-10px) scale(0.95)'};
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1002;
`;

const LanguageOption = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: transparent;
  border: none;
  color: ${props => props.theme === 'dark' ? '#cbd5e1' : '#e2e8f0'};
  text-align: left;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  font-size: 0.875rem;
  
  &:hover {
    background: rgba(230, 57, 70, 0.1);
    color: #e63946;
    transform: translateX(4px);
  }
  
  &.active {
    background: rgba(230, 57, 70, 0.15);
    color: #e63946;
  }
  
  .flag {
    font-size: 1.1rem;
    line-height: 1;
  }
  
  .language-name {
    flex: 1;
  }
  
  .check {
    opacity: ${props => props.$isActive ? 1 : 0};
    color: #e63946;
    font-size: 0.875rem;
    transition: opacity 0.3s ease;
  }
`;

const languages = {
  es: {
    name: 'Español',
    flag: '🇪🇸',
    code: 'es'
  },
  en: {
    name: 'English',
    flag: '🇺🇸',
    code: 'en'
  }
};

const LanguageSelector = ({ theme = 'light', compact = false, className = '' }) => {
  const { language, changeLanguage } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-language-selector]')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsOpen(false);
  };

  const currentLanguage = languages[language] || languages.es;

  return (
    <SelectorContainer className={className} data-language-selector>
      <SelectorButton
        onClick={() => setIsOpen(!isOpen)}
        theme={theme}
        $compact={compact}
        $isOpen={isOpen}
        title="Cambiar idioma / Change language"
      >
        {compact ? (
          <FaGlobe />
        ) : (
          <>
            <span className="flag">{currentLanguage.flag}</span>
            <span className="language-text">{currentLanguage.name}</span>
          </>
        )}
        <FaChevronDown className="chevron" />
      </SelectorButton>

      <DropdownMenu theme={theme} $isOpen={isOpen}>
        {Object.values(languages).map((lang) => (
          <LanguageOption
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            theme={theme}
            $isActive={language === lang.code}
            className={language === lang.code ? 'active' : ''}
          >
            <span className="flag">{lang.flag}</span>
            <span className="language-name">{lang.name}</span>
            <span className="check">✓</span>
          </LanguageOption>
        ))}
      </DropdownMenu>
    </SelectorContainer>
  );
};

export default LanguageSelector; 